import { useSuspenseQuery, useMutation } from '@tanstack/react-query';
import { type TeamMember, DEFAULT_USERS_TEAM } from './entity';
import {
  getTeamMember,
  postInviteTeamMember,
  deleteTeamMember,
  patchTeamMemberSkill,
  getTeamMembers,
} from './api';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { queryClient } from '@shared/queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { getLocalTimestamp, trackMixpanelEvent } from '@libs/mixpanel/mixpanel';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';
import { isDemo } from '@features/demo/store';

export const useSuspenseTeamMemberQuery = () => {
  return useSuspenseQuery<TeamMember>({
    queryKey: ['teamMember'],
    queryFn: () => getTeamMember(),
  });
};

export type SkillUpdateInput = {
  memberId: string;
  canAccessStocks: boolean;
  canAccessClients: boolean;
  canAccessVisits: boolean;
};

export const useAddTeamMemberMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('team.mutation');
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: (data: { email: string; consentId: string }) => {
      return postInviteTeamMember(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMember'] });
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      trackMixpanelEvent('team-member-added', {
        staff_count: 1,
        timestamp_local: getLocalTimestamp(),
      });

      addSnackBarMessage({ text: t('addSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: t('addError'), type: 'error' });
    },
  });
};

export const useDeleteTeamMemberMutation = () => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('team.mutation');

  return useMutation({
    mutationFn: (deletedUserId: string) => deleteTeamMember(deletedUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      queryClient.invalidateQueries({ queryKey: ['stockItems', undefined] });
      addSnackBarMessage({ text: t('removeSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: t('removeError'), type: 'error' });
    },
  });
};

export const useUpdateTeamMemberSkill = (teamId?: string) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('team.mutation');

  return useMutation({
    mutationFn: (data: SkillUpdateInput) => {
      if (!teamId) {
        throw new Error('Team ID not found.');
      }
      return patchTeamMemberSkill(data, teamId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      addSnackBarMessage({ text: t('permissionsSuccess'), type: 'success' });
    },
    onError: error => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      addSnackBarMessage({ text: t('permissionsError'), type: 'error' });
    },
  });
};

export const useSuspenseTeamMembersQuery = (teamId?: string) => {
  const resolvedTeamId = teamId || DEFAULT_USERS_TEAM;

  return useSuspenseQuery<(TeamMember & { user: { name: string } })[]>({
    queryKey: ['teamMembers'],
    queryFn: () => getTeamMembers(resolvedTeamId),
  });
};
